/**
 * GraphQL Query Hooks
 *
 * Replaces REST API fetch calls with GraphQL queries.
 * Uses urql's gql at runtime for graphcache compatibility.
 */

import { useQuery, gql } from 'urql';
import createDebug from 'debug';
import type {
	QueueResponse,
	CategoryResponse,
	TagResponse,
	LinkSummaryResponse,
	LinkResponse,
	RelatedLink,
} from '@/api/client';
import { urqlClient } from '@/graphql/client';
import { encodeGlobalId, parseGlobalId } from '@/lib/global-id';

const debug = createDebug('app:queries');

// ============================================================================
// GraphQL Query Documents
// ============================================================================

const GetQueuesQuery = gql`
	query GetQueues {
		queues {
			edges {
				node {
					id
					slug
					name
					color
					icon
					position
					linkCount
				}
			}
			totalCount
		}
	}
`;

const GetCategoriesQuery = gql`
	query GetCategories {
		categories {
			id
			name
			color
			icon
			nsfw
			linkCount
		}
	}
`;

const GetTagsQuery = gql`
	query GetTags($includeNsfw: Boolean) {
		tags(includeNsfw: $includeNsfw) {
			edges {
				node {
					id
					name
					color
					icon
					isNsfw
					isCategory
					linkCount
					isPersonal
					createdAt
				}
			}
			totalCount
		}
	}
`;

const GetLinksQuery = gql`
	query GetLinks(
		$first: Int
		$after: String
		$queueId: Int
		$category: String
		$archived: Boolean
		$tagFilter: String
	) {
		links(
			first: $first
			after: $after
			queueId: $queueId
			category: $category
			archived: $archived
			tagFilter: $tagFilter
		) {
			edges {
				node {
					id
					url
					title
					description
					queueId
					thumbnailUrl
					isEnriching
					updatedAt
					archivedAt
					tags(first: 50) {
						edges {
							node {
								id
								name
								color
								icon
								isNsfw
								isCategory
								isPersonal
							}
						}
					}
				}
				cursor
			}
			pageInfo {
				hasNextPage
				endCursor
			}
			totalCount
		}
	}
`;

const GetLinkQuery = gql`
	query GetLink($id: ID!) {
		link(id: $id) {
			id
			url
			title
			description
			queueId
			metadata
			thumbnailUrl
			isEnriching
			createdAt
			updatedAt
			archivedAt
			categories
			queue {
				id
				slug
				name
				color
				icon
			}
			tags(first: 50) {
				edges {
					node {
						id
						name
						color
						icon
						isNsfw
						isCategory
						isPersonal
					}
				}
			}
		}
	}
`;

const GetLinkWithRelatedQuery = gql`
	query GetLinkWithRelated($id: ID!, $queueId: Int) {
		link(id: $id) {
			id
			url
			title
			description
			queueId
			metadata
			thumbnailUrl
			isEnriching
			createdAt
			updatedAt
			archivedAt
			categories
			queue {
				id
				slug
				name
				color
				icon
			}
			tags(first: 50) {
				edges {
					node {
						id
						name
						color
						icon
						isNsfw
						isCategory
						isPersonal
					}
				}
			}
			relatedLinks(queueId: $queueId) {
				edges {
					node {
						id
						url
						title
						thumbnailUrl
						archivedAt
						matchingTags
						createdAt
					}
				}
				sourceUserTags
				totalCount
			}
		}
	}
`;

const SearchLinksQuery = gql`
	query SearchLinks($query: String!, $first: Int, $tagFilter: String) {
		searchLinks(query: $query, first: $first, tagFilter: $tagFilter) {
			edges {
				node {
					id
					url
					title
					description
					queueId
					thumbnailUrl
					isEnriching
					updatedAt
					archivedAt
					queue {
						id
						slug
						name
						color
						icon
					}
					tags(first: 50) {
						edges {
							node {
								id
								name
								color
								icon
								isNsfw
								isCategory
								isPersonal
							}
						}
					}
				}
			}
			totalCount
		}
	}
`;

// ============================================================================
// Transform Functions (GraphQL → REST response types)
// ============================================================================

interface GqlQueue {
	id: string;
	slug: string;
	name: string;
	color: string;
	icon: string | null;
	position: number;
	linkCount?: number;
}

function transformQueue(node: GqlQueue): QueueResponse {
	const { id: numericId } = parseGlobalId(node.id);
	return {
		id: numericId,
		slug: node.slug,
		name: node.name,
		color: node.color,
		icon: node.icon,
		position: node.position,
		linkCount: node.linkCount,
	};
}

interface GqlTag {
	id: string;
	name: string;
	color: string | null;
	icon: string | null;
	isNsfw?: boolean;
	isCategory?: boolean;
	linkCount?: number;
	isPersonal?: boolean;
	createdAt?: string;
}

function transformTag(node: GqlTag): TagResponse {
	const { id: numericId } = parseGlobalId(node.id);
	return {
		id: numericId,
		name: node.name,
		color: node.color,
		icon: node.icon,
		linkCount: node.linkCount,
		isPersonal: node.isPersonal,
	};
}

interface GqlLinkSummary {
	id: string;
	url: string;
	title: string;
	description: string | null;
	queueId: number;
	thumbnailUrl: string | null;
	isEnriching: boolean;
	updatedAt: string;
	archivedAt?: string | null;
	snoozedUntil?: string | null;
	tags?: { edges: Array<{ node: GqlTag }> };
}

function transformLinkSummary(node: GqlLinkSummary): LinkSummaryResponse {
	const { id: numericId } = parseGlobalId(node.id);
	return {
		id: numericId,
		url: node.url,
		title: node.title,
		description: node.description,
		queueId: node.queueId,
		thumbnailUrl: node.thumbnailUrl,
		isEnriching: node.isEnriching,
		updatedAt: node.updatedAt,
		snoozedUntil: node.snoozedUntil,
		tags: node.tags?.edges.map((e) => transformTag(e.node)),
	};
}

interface GqlLinkFull extends GqlLinkSummary {
	metadata: Record<string, unknown> | null;
	createdAt: string;
	categories?: string[];
	queue?: GqlQueue;
}

function transformLinkFull(node: GqlLinkFull): LinkResponse {
	const { id: numericId } = parseGlobalId(node.id);
	return {
		id: numericId,
		url: node.url,
		title: node.title,
		description: node.description,
		queueId: node.queueId,
		metadata: node.metadata,
		source: null, // Not exposed in GraphQL
		thumbnailUrl: node.thumbnailUrl,
		isEnriching: node.isEnriching,
		createdAt: node.createdAt,
		updatedAt: node.updatedAt,
		archivedAt: node.archivedAt ?? null,
		tags: node.tags?.edges.map((e) => transformTag(e.node)),
	};
}

interface GqlRelatedLink {
	id: string;
	url: string;
	title: string;
	thumbnailUrl: string | null;
	archivedAt: string | null;
	matchingTags: string[];
	createdAt: string;
}

function transformRelatedLink(node: GqlRelatedLink): RelatedLink {
	const { id: numericId } = parseGlobalId(node.id);
	return {
		id: numericId,
		url: node.url,
		title: node.title,
		thumbnailUrl: node.thumbnailUrl,
		archivedAt: node.archivedAt,
		matchingTags: node.matchingTags,
		createdAt: node.createdAt,
	};
}

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all queues
 */
export function useQueuesQuery() {
	const [result, reexecute] = useQuery({
		query: GetQueuesQuery,
	});

	const queues = result.data?.queues?.edges?.map((e: { node: GqlQueue }) =>
		transformQueue(e.node)
	) ?? [];

	debug('useQueuesQuery: fetching=%s queues=%d', result.fetching, queues.length);

	return {
		queues,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

/**
 * Fetch all categories
 */
export function useCategoriesQuery() {
	const [result, reexecute] = useQuery({
		query: GetCategoriesQuery,
	});

	const categories: CategoryResponse[] = result.data?.categories ?? [];

	debug('useCategoriesQuery: fetching=%s categories=%d', result.fetching, categories.length);

	return {
		categories,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

/**
 * Fetch all tags
 */
export function useTagsQuery(options?: { includeNsfw?: boolean }) {
	const [result, reexecute] = useQuery({
		query: GetTagsQuery,
		variables: { includeNsfw: options?.includeNsfw ?? false },
	});

	const tags = result.data?.tags?.edges?.map((e: { node: GqlTag }) =>
		transformTag(e.node)
	) ?? [];

	debug('useTagsQuery: fetching=%s tags=%d', result.fetching, tags.length);

	return {
		tags,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

/**
 * Fetch links for a specific queue (or all queues)
 * Use `pause` option to defer fetching until needed
 */
export function useQueueLinksQuery(
	queueId: number | null,
	options?: { pause?: boolean; archived?: boolean }
) {
	const [result, reexecute] = useQuery({
		query: GetLinksQuery,
		variables: {
			queueId: queueId ?? undefined,
			first: 500,
			archived: options?.archived ?? false,
		},
		pause: options?.pause ?? false,
	});

	const links = result.data?.links?.edges?.map((e: { node: GqlLinkSummary }) =>
		transformLinkSummary(e.node)
	) ?? [];

	debug('useQueueLinksQuery: queueId=%d fetching=%s links=%d', queueId, result.fetching, links.length);

	return {
		links,
		total: result.data?.links?.totalCount ?? 0,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

/**
 * Fetch full details for a single link
 * Use `pause` option or pass null linkId to defer fetching
 */
export function useLinkDetailsQuery(linkId: number | null) {
	const [result, reexecute] = useQuery({
		query: GetLinkQuery,
		variables: { id: linkId ? encodeGlobalId('Link', linkId) : '' },
		pause: linkId === null,
	});

	const link = result.data?.link ? transformLinkFull(result.data.link) : null;

	debug('useLinkDetailsQuery: linkId=%d fetching=%s found=%s', linkId, result.fetching, !!link);

	return {
		link,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

/**
 * Fetch link details with related links (for detail panel with sidebar)
 */
export function useLinkWithRelatedQuery(linkId: number | null, queueId?: number) {
	const [result, reexecute] = useQuery({
		query: GetLinkWithRelatedQuery,
		variables: {
			id: linkId ? encodeGlobalId('Link', linkId) : '',
			queueId,
		},
		pause: linkId === null,
	});

	const link = result.data?.link ? transformLinkFull(result.data.link) : null;
	const relatedLinks = result.data?.link?.relatedLinks?.edges?.map(
		(e: { node: GqlRelatedLink }) => transformRelatedLink(e.node)
	) ?? [];
	const sourceUserTags = result.data?.link?.relatedLinks?.sourceUserTags ?? [];

	debug(
		'useLinkWithRelatedQuery: linkId=%d fetching=%s related=%d',
		linkId,
		result.fetching,
		relatedLinks.length
	);

	return {
		link,
		relatedLinks,
		sourceUserTags,
		totalRelated: result.data?.link?.relatedLinks?.totalCount ?? 0,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

/**
 * Search links by query string
 * Use `pause` option to defer until search query is entered
 */
export function useSearchLinksQuery(
	query: string,
	options?: { first?: number; tagFilter?: string; pause?: boolean }
) {
	const [result, reexecute] = useQuery({
		query: SearchLinksQuery,
		variables: {
			query,
			first: options?.first ?? 20,
			tagFilter: options?.tagFilter,
		},
		pause: options?.pause ?? !query.trim(),
	});

	const results = result.data?.searchLinks?.edges?.map((e: { node: GqlLinkFull }) =>
		transformLinkFull(e.node)
	) ?? [];

	debug('useSearchLinksQuery: query="%s" fetching=%s results=%d', query, result.fetching, results.length);

	return {
		results,
		total: result.data?.searchLinks?.totalCount ?? 0,
		loading: result.fetching,
		error: result.error,
		refetch: reexecute,
	};
}

// ============================================================================
// Imperative Fetch Functions (for use in useEffects, not as hooks)
// ============================================================================

/**
 * Fetch links for a specific queue (imperative, for use in useEffect)
 * Note: queueId=0 is the "incoming" queue in the database
 */
export async function fetchQueueLinksGQL(
	queueId: number,
	options?: { archived?: boolean; includeSnoozed?: boolean }
): Promise<{ links: LinkSummaryResponse[]; total: number }> {
	const result = await urqlClient
		.query(GetLinksQuery, {
			queueId, // Pass as-is; queueId=0 is the incoming queue
			first: 500,
			archived: options?.archived ?? false,
			includeSnoozed: options?.includeSnoozed ?? false,
		})
		.toPromise();

	if (result.error) {
		throw new Error(result.error.message);
	}

	const links = result.data?.links?.edges?.map((e: { node: GqlLinkSummary }) =>
		transformLinkSummary(e.node)
	) ?? [];

	debug('fetchQueueLinksGQL: queueId=%d links=%d', queueId, links.length);

	return {
		links,
		total: result.data?.links?.totalCount ?? 0,
	};
}

/**
 * Fetch full link details (imperative, for use in useEffect)
 */
export async function fetchLinkDetailsGQL(linkId: number): Promise<LinkResponse | null> {
	const result = await urqlClient
		.query(GetLinkQuery, { id: encodeGlobalId('Link', linkId) })
		.toPromise();

	if (result.error) {
		throw new Error(result.error.message);
	}

	const link = result.data?.link ? transformLinkFull(result.data.link) : null;

	debug('fetchLinkDetailsGQL: linkId=%d found=%s', linkId, !!link);

	return link;
}

/**
 * Fetch archived links (imperative, for use in useEffect)
 */
export async function fetchArchivedLinksGQL(): Promise<{ links: LinkSummaryResponse[]; total: number }> {
	const result = await urqlClient
		.query(GetLinksQuery, {
			first: 500,
			archived: true,
		})
		.toPromise();

	if (result.error) {
		throw new Error(result.error.message);
	}

	const links = result.data?.links?.edges?.map((e: { node: GqlLinkSummary }) =>
		transformLinkSummary(e.node)
	) ?? [];

	debug('fetchArchivedLinksGQL: links=%d', links.length);

	return {
		links,
		total: result.data?.links?.totalCount ?? 0,
	};
}

/**
 * Search links by query string (imperative, for debounced search)
 */
export async function searchLinksGQL(
	query: string,
	first: number = 20,
	tagFilter?: string
): Promise<{ results: LinkResponse[]; total: number }> {
	const result = await urqlClient
		.query(SearchLinksQuery, {
			query,
			first,
			tagFilter,
		})
		.toPromise();

	if (result.error) {
		throw new Error(result.error.message);
	}

	const results = result.data?.searchLinks?.edges?.map((e: { node: GqlLinkFull }) =>
		transformLinkFull(e.node)
	) ?? [];

	debug('searchLinksGQL: query="%s" results=%d', query, results.length);

	return {
		results,
		total: result.data?.searchLinks?.totalCount ?? 0,
	};
}
